<template>
    <div class="horse-edit-owners flex gap-3 my-4 justify-content-evenly" :class="vertical ? 'flex-column gap-8' : ''">
        <div class="flex flex-column gap-3 breeders-box" :class="vertical ? 'w-full' : 'w-5'">
            <div class="header">
                {{required ? '*' : null}}
                {{$t('message.horseEdit.main.breedersHeader')}}:</div>
            <div v-for="(breeder, index) in horse.breeders" :key="index" class="flex w-full align-items-center gap-2">
                <CustomAutocompleteOwner name="breeder" v-model="horse.breeders[index]" class="w-full"
                                      :placeholder="$t('message.inputs.selectFromExisting')"
                                      itemValue=""
                                      :key="refreshKey" :disabled="disableEdit"/>
                <CustomIconButton icon="pi pi-trash" onlyIcon @click="removeBreeder(index)" v-if="!disableEdit" />
                <CustomTextButton v-if="index === horse.breeders?.length - 1 && breeder == null && disableEdit" class="create-new"
                                  :label="$t('message.horseEdit.main.createBreederButton')" @click="openDialog"/>
            </div>
            <div class="relative">
                <CustomValidationMessage v-if="breedersErrorMessage" errorMessage="At least one breeder is required" />
            </div>
            <div><CustomTextButton v-if="!lastBreederNull && !disableEdit"
                                   :label="$t('message.horseEdit.main.addBreederButton')" @click="addBreeder"/></div>

        </div>
        <div class="flex flex-column gap-3 owners-box" :class="vertical ? 'w-full' : 'w-5'">
            <OwnerAddDialog v-model="dialogVisible" @addedNewOwner="addedNewOwner"></OwnerAddDialog>
            <div class="flex justify-content-between" style="margin-right: 2.5rem">
                <div class="header">
                    {{required ? '*' : null}}
                    {{$t('message.horseEdit.main.ownersHeader')}}:</div>
                <CustomCheckbox name="sameAsBreeders" :label="$t('message.horseEdit.main.sameAsBreeders')"
                                v-model="sameAsBreeders" @change="this.copyBreedersToOwners" :upperCase="false"
                                v-if="!disableEdit"/>
            </div>
            <div v-for="(owner, index) in horse.owners" :key="index" class="flex w-full align-items-center gap-2">
                <CustomAutocompleteOwner name="owner" v-model="horse.owners[index]" class="w-full"
                                      :placeholder="$t('message.inputs.selectFromExisting')"
                                      itemValue=""
                                      :key="refreshKey" :disabled="disableEdit"/>
                <CustomIconButton icon="pi pi-trash" onlyIcon @click="removeOwner(index)" v-if="!disableEdit" />
                <CustomTextButton v-if="index === horse.owners?.length - 1 && owner == null && !disableEdit" class="create-new"
                                  :label="$t('message.horseEdit.main.createOwnerButton')" @click="openDialog"/>
            </div>
            <div class="relative">
                <CustomValidationMessage v-show="ownersErrorMessage" errorMessage="At least one owner is required" />
            </div>
            <div><CustomTextButton v-if="!lastOwnerNull && !disableEdit"
                                   :label="$t('message.horseEdit.main.addOwnerButton')" @click="addOwner"/></div>
        </div>
    </div>
</template>

<script>
import Button from "primevue/button";
import CustomSelectOneCountry from "@/components/form/CustomSelectOneCountry.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomAutocompleteUser from "@/components/form/CustomAutocompleteUser.vue";
import CustomCheckbox from "@/components/form/CustomCheckbox.vue";
import OwnerAddDialog from "@/views/owner/OwnerAddDialog.vue";
import CustomIconButton from "@/components/form/CustomIconButton.vue";
import CustomTextButton from "@/components/form/CustomTextButton.vue";
import CustomValidationMessage from "@/components/form/inner/CustomValidationMessage.vue";
import CustomAutocompleteOwner from "@/components/form/CustomAutocompleteOwner.vue";

export default {
    name: "HorseEditOwners",
    components: {
        CustomTextButton,
        CustomIconButton,
        OwnerAddDialog,
        CustomCheckbox,
        CustomAutocompleteUser,
        CustomCalendar,
        CustomSelectOneCountry,
        Button,
        CustomValidationMessage,
        CustomAutocompleteOwner
    },

    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        vertical: {
            type: Boolean,
            default: false,
        },
        disableEdit: {
            type: Boolean,
            default: false,
        },
        breedersErrorMessage: {
            type: Boolean,
            default: null,
        },
        ownersErrorMessage: {
            type: Boolean,
            default: null,
        },
        required: {
            type: Boolean,
            default: true,
        }
    },

    emits: ["update:modelValue"],

    data() {
        return {
            dialogVisible: false,
            sameAsBreeders: false,
            refreshKey: 0,
        };
    },

    mounted() {
        this.addBreederIfThereIsNoBreeder();
        this.addOwnerIfThereIsNoOwner();
    },

    methods: {
        addBreeder() {
            this.horse.breeders.push(null);
        },
        removeBreeder(index) {
            this.horse.breeders.splice(index, 1);
            this.addBreederIfThereIsNoBreeder();
        },
        addOwner() {
            this.horse.owners.push(null);
            },
        removeOwner(index) {
            this.horse.owners.splice(index, 1);
            this.addOwnerIfThereIsNoOwner();
        },
        copyBreedersToOwners() {
            if (this.sameAsBreeders) {
                this.horse.owners = [...this.horse.breeders];
            }
        },
        addBreederIfThereIsNoBreeder() {
            if (this.horse.breeders?.length === 0) {
                this.addBreeder();
            }
        },
        addOwnerIfThereIsNoOwner() {
            if (this.horse.owners?.length === 0) {
                this.addOwner();
            }
        },
        openDialog() {
            this.dialogVisible = true;
        },
        getRequiredMessage(value) {
            if (this.showErrorMessages && (value === null || value === "")) {
                return this.$t("message.validations.required");
            }
            return "";
        },
        addedNewOwner() {
            this.refreshKey += 1;
        }
    },

    computed: {
        horse: {
            get() { return this.modelValue; },
            set(value) { this.$emit("update:modelValue", value); },
        },
        lastBreederNull() {
            if (this.horse.breeders == null || this.horse.breeders.length === 0) return false;
            const lastIndex = this.horse.breeders.length - 1;
            return this.horse.breeders[lastIndex] == null;
        },
        lastOwnerNull() {
            if (this.horse.owners == null || this.horse.owners.length === 0) return false;
            const lastIndex = this.horse.owners.length - 1;
            return this.horse.owners[lastIndex] == null;
        }
    },

    watch: {
        horse() {
            this.addBreederIfThereIsNoBreeder();
            this.addOwnerIfThereIsNoOwner();
        },
        'horse.breeders': {
            handler(newBreeders) {
                if (this.sameAsBreeders) {
                    this.horse.owners = newBreeders.map(breeder => breeder);
                }
            },
            deep: true, // Enables array tracking
            immediate: true,
        },
    },
};
</script>

<style lang="scss">
@import "../../../../../assets/theme/mytheme/variables";
.horse-edit-owners {
    .header {
        font-size: 18px;
        text-transform: uppercase;
    }
    .create-new {
        min-width: 110px;
    }
    @media screen and (max-width: 900px) {
        flex-direction: column;
        align-items: center;
        .breeders-box, .owners-box {
            width: 80% !important;
        }
    }
}
</style>
